import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { CheckImage } from '../../types';

interface CheckImageEditorProps {
  checkImage: CheckImage;
  onSave: (updatedCheckImage: CheckImage) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const CheckImageEditor: React.FC<CheckImageEditorProps> = ({ checkImage, onSave, onCancel, onDelete }) => {
  const [formData, setFormData] = useState<CheckImage>(checkImage);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData(checkImage);
    setErrors({});
  }, [checkImage]);

  const handleInputChange = (field: keyof CheckImage, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate amount if provided
    if (formData.amount && formData.amount.trim()) {
      const amountStr = formData.amount.replace(/[$,]/g, '');
      if (isNaN(Number(amountStr))) {
        newErrors.amount = 'Please enter a valid amount';
      }
    }

    // Validate bounding box coordinates if provided
    const bboxFields = ['bbox_x', 'bbox_y', 'bbox_width', 'bbox_height'];
    bboxFields.forEach(field => {
      const value = formData[field as keyof CheckImage] as number;
      if (value !== undefined && value !== null && (isNaN(value) || value < 0)) {
        newErrors[field] = 'Must be a positive number';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Clean up the data before saving
    const cleanedData = {
      ...formData,
      // Trim string fields
      check_number: formData.check_number?.trim() || undefined,
      amount: formData.amount?.trim() || undefined,
      bank_name: formData.bank_name?.trim() || undefined,
      account_number: formData.account_number?.trim() || undefined,
      routing_number: formData.routing_number?.trim() || undefined,
      date: formData.date?.trim() || undefined,
      payee: formData.payee?.trim() || undefined,
      // Ensure numeric fields are properly typed
      bbox_x: formData.bbox_x ? Number(formData.bbox_x) : undefined,
      bbox_y: formData.bbox_y ? Number(formData.bbox_y) : undefined,
      bbox_width: formData.bbox_width ? Number(formData.bbox_width) : undefined,
      bbox_height: formData.bbox_height ? Number(formData.bbox_height) : undefined,
    };

    onSave(cleanedData);
  };

  return (
    <Form onSubmit={handleSubmit}>
      {Object.keys(errors).length > 0 && (
        <Alert variant="danger" className="mb-3">
          <Alert.Heading>Please fix the following errors:</Alert.Heading>
          <ul className="mb-0">
            {Object.entries(errors).map(([field, error]) => (
              <li key={field}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Check Number</Form.Label>
            <Form.Control
              type="text"
              value={formData.check_number || ''}
              onChange={(e) => handleInputChange('check_number', e.target.value)}
              placeholder="Enter check number"
              isInvalid={!!errors.check_number}
            />
            <Form.Control.Feedback type="invalid">{errors.check_number}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Amount</Form.Label>
            <Form.Control
              type="text"
              value={formData.amount || ''}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              placeholder="Enter amount (e.g., $100.00)"
              isInvalid={!!errors.amount}
            />
            <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control
              type="text"
              value={formData.date || ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              placeholder="Enter date"
              isInvalid={!!errors.date}
            />
            <Form.Control.Feedback type="invalid">{errors.date}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Pay To (Payee)</Form.Label>
            <Form.Control
              type="text"
              value={formData.payee || ''}
              onChange={(e) => handleInputChange('payee', e.target.value)}
              placeholder="Enter payee name"
              isInvalid={!!errors.payee}
            />
            <Form.Control.Feedback type="invalid">{errors.payee}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>Bank Name</Form.Label>
        <Form.Control
          type="text"
          value={formData.bank_name || ''}
          onChange={(e) => handleInputChange('bank_name', e.target.value)}
          placeholder="Enter bank name"
          isInvalid={!!errors.bank_name}
        />
        <Form.Control.Feedback type="invalid">{errors.bank_name}</Form.Control.Feedback>
      </Form.Group>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Account Number</Form.Label>
            <Form.Control
              type="text"
              value={formData.account_number || ''}
              onChange={(e) => handleInputChange('account_number', e.target.value)}
              placeholder="Enter account number"
              isInvalid={!!errors.account_number}
            />
            <Form.Control.Feedback type="invalid">{errors.account_number}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Routing Number</Form.Label>
            <Form.Control
              type="text"
              value={formData.routing_number || ''}
              onChange={(e) => handleInputChange('routing_number', e.target.value)}
              placeholder="Enter routing number"
              isInvalid={!!errors.routing_number}
            />
            <Form.Control.Feedback type="invalid">{errors.routing_number}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      {/* Bounding Box Coordinates */}
      <h6 className="mt-4 mb-3">Bounding Box Coordinates (Optional)</h6>
      <Row>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>X Position</Form.Label>
            <Form.Control
              type="number"
              value={formData.bbox_x || ''}
              onChange={(e) => handleInputChange('bbox_x', e.target.value)}
              placeholder="X coordinate"
              min="0"
              isInvalid={!!errors.bbox_x}
            />
            <Form.Control.Feedback type="invalid">{errors.bbox_x}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Y Position</Form.Label>
            <Form.Control
              type="number"
              value={formData.bbox_y || ''}
              onChange={(e) => handleInputChange('bbox_y', e.target.value)}
              placeholder="Y coordinate"
              min="0"
              isInvalid={!!errors.bbox_y}
            />
            <Form.Control.Feedback type="invalid">{errors.bbox_y}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Width</Form.Label>
            <Form.Control
              type="number"
              value={formData.bbox_width || ''}
              onChange={(e) => handleInputChange('bbox_width', e.target.value)}
              placeholder="Box width"
              min="0"
              isInvalid={!!errors.bbox_width}
            />
            <Form.Control.Feedback type="invalid">{errors.bbox_width}</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Height</Form.Label>
            <Form.Control
              type="number"
              value={formData.bbox_height || ''}
              onChange={(e) => handleInputChange('bbox_height', e.target.value)}
              placeholder="Box height"
              min="0"
              isInvalid={!!errors.bbox_height}
            />
            <Form.Control.Feedback type="invalid">{errors.bbox_height}</Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>

      <div className="d-flex justify-content-between mt-4">
        {onDelete && (
          <Button variant="outline-danger" type="button" onClick={onDelete}>
            Delete
          </Button>
        )}
        <div className="d-flex gap-2 ms-auto">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Save Changes
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default CheckImageEditor;